<template>
  <div v-if="show" class="card">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="card-title mb-0">Create Referral</h4>
        <button type="button" class="btn btn-sm btn-light" @click="$emit('update:show', false); $emit('cancel')">Close</button>
      </div>

      <!-- Patient Selection -->
      <div class="mb-3">
        <label class="form-label">Patient</label>
        <div class="position-relative">
          <input
            v-model="patientQuery"
            type="text"
            class="form-control"
            placeholder="Search patient by name or MRN"
            @focus="dropdownOpen = true"
            @input="onPatientQuery"
          />
          <ul v-if="dropdownOpen && filteredPatients.length" class="list-group position-absolute w-100" style="z-index: 1000; max-height: 220px; overflow: auto;">
            <li
              v-for="p in filteredPatients"
              :key="p.id"
              class="list-group-item list-group-item-action"
              @click="selectPatient(p)"
            >
              {{ displayPatient(p) }}
            </li>
          </ul>
        </div>
        <small v-if="form.patient" class="text-muted d-block mt-1">
          Selected: {{ displayPatient(form.patient) }}
        </small>
      </div>

      <!-- Auto-filled Details -->
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Date of Birth</label>
          <input type="text" class="form-control" :value="form.patient ? form.patient.dob : ''" disabled />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Gender</label>
          <input type="text" class="form-control" :value="form.patient ? form.patient.gender : ''" disabled />
        </div>
      </div>

      <!-- Referral Type -->
      <div class="mb-3">
        <label class="form-label d-block">Referral Type</label>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" id="type-internal" value="internal" v-model="form.type" />
          <label class="form-check-label" for="type-internal">Internal</label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" id="type-external" value="external" v-model="form.type" />
          <label class="form-check-label" for="type-external">External</label>
        </div>
      </div>

      <!-- Conditional Fields -->
      <div v-if="form.type === 'external'" class="mb-3">
        <label class="form-label">Hospital</label>
        <select v-model="form.hospitalId" class="form-select">
          <option value="" disabled>Select hospital</option>
          <option v-for="h in hospitals" :key="h.id" :value="h.id">{{ h.name }}</option>
        </select>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Department</label>
          <select v-model="form.departmentId" class="form-select">
            <option value="" disabled>Select department</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Doctor</label>
          <select v-model="form.doctorId" class="form-select">
            <option value="" disabled>Select doctor</option>
            <option v-for="d in doctorsFiltered" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Reason for Referral</label>
          <input v-model="form.reason" type="text" class="form-control" placeholder="e.g. Cardiology evaluation" />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Urgency Level</label>
          <select v-model="form.urgency" class="form-select">
            <option value="" disabled>Select urgency</option>
            <option v-for="u in urgencyOptions" :key="u" :value="u">{{ u }}</option>
          </select>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Recommended Specialty/Specialist</label>
        <input v-model="form.recommendedSpecialty" type="text" class="form-control" placeholder="e.g. Interventional Cardiology / Dr. X" />
      </div>

      <div class="mb-3">
        <label class="form-label">Relevant Medical History/Notes</label>
        <textarea v-model="form.history" rows="3" class="form-control" placeholder="Enter relevant medical history"></textarea>
      </div>

      <div class="mb-3">
        <label class="form-label">Attachments</label>
        <input ref="fileInput" @change="onFilesChange" type="file" class="form-control" multiple />
        <small v-if="form.attachments.length" class="text-muted d-block mt-1">{{ form.attachments.length }} file(s) selected</small>
      </div>

      <div class="mb-3">
        <label class="form-label">Referring Provider Notes (Optional)</label>
        <textarea v-model="form.refProviderNotes" rows="2" class="form-control" placeholder="Optional notes"></textarea>
      </div>

      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-light me-2" type="button" @click="$emit('update:show', false); $emit('cancel')">Cancel</button>
        <button class="btn btn-primary" type="button" :disabled="!canSubmit" @click="onSubmit">Create Referral</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CreateReferralForm',
  props: {
    show: { type: Boolean, default: false },
    patients: { type: Array, default: () => [] },
    departments: { type: Array, default: () => [] },
    doctors: { type: Array, default: () => [] },
    hospitals: { type: Array, default: () => [] }
  },
  data() {
    return {
      patientQuery: '',
      dropdownOpen: false,
      urgencyOptions: ['Low', 'Medium', 'High', 'Critical'],
      form: {
        patient: null,
        type: 'internal',
        hospitalId: '',
        departmentId: '',
        doctorId: '',
        reason: '',
        urgency: '',
        recommendedSpecialty: '',
        history: '',
        attachments: [],
        refProviderNotes: ''
      }
    };
  },
  computed: {
    filteredPatients() {
      const q = this.patientQuery.trim().toLowerCase();
      if (!q) return this.patients.slice(0, 10);
      return this.patients.filter(p => {
        return (
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.mrn && String(p.mrn).toLowerCase().includes(q))
        );
      }).slice(0, 20);
    },
    doctorsFiltered() {
      const deptId = this.form.departmentId;
      if (!deptId) return this.doctors;
      return this.doctors.filter(d => !d.departmentId || d.departmentId === deptId);
    },
    canSubmit() {
      const f = this.form;
      if (!f.patient || !f.reason || !f.urgency) return false;
      if (f.type === 'internal') {
        return !!(f.departmentId && f.doctorId);
      } else {
        return !!(f.hospitalId && f.departmentId && f.doctorId);
      }
    }
  },
  methods: {
    displayPatient(p) {
      if (!p) return '';
      return `${p.name} - (Mrn: ${p.mrn})`;
    },
    onPatientQuery() {
      this.dropdownOpen = true;
    },
    selectPatient(p) {
      this.form.patient = p;
      this.patientQuery = this.displayPatient(p);
      this.dropdownOpen = false;
    },
    onFilesChange(e) {
      const files = Array.from(e.target.files || []);
      this.form.attachments = files;
    },
    reset() {
      this.patientQuery = '';
      this.dropdownOpen = false;
      this.form = {
        patient: null,
        type: 'internal',
        hospitalId: '',
        departmentId: '',
        doctorId: '',
        reason: '',
        urgency: '',
        recommendedSpecialty: '',
        history: '',
        attachments: [],
        refProviderNotes: ''
      };
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    onSubmit() {
      const payload = {
        patientId: this.form.patient ? this.form.patient.id : null,
        patient: this.form.patient,
        type: this.form.type,
        hospitalId: this.form.type === 'external' ? this.form.hospitalId : null,
        departmentId: this.form.departmentId,
        doctorId: this.form.doctorId,
        reason: this.form.reason,
        urgency: this.form.urgency,
        recommendedSpecialty: this.form.recommendedSpecialty,
        history: this.form.history,
        attachments: this.form.attachments,
        refProviderNotes: this.form.refProviderNotes,
        createdAt: new Date().toISOString()
      };
      this.$emit('submit', payload);
      this.$emit('update:show', false);
      this.reset();
    }
  },
  watch: {
    show(val) {
      if (!val) return;
      // reset on open
      this.reset();
    },
    'form.departmentId'() {
      if (!this.form.doctorId) return;
      const ok = this.doctorsFiltered.some(d => d.id === this.form.doctorId);
      if (!ok) this.form.doctorId = '';
    }
  }
};
</script>

<style scoped>
.list-group-item {
  cursor: pointer;
}
</style>
